import React, { useEffect, useState } from "react";
import { Pencil, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export default function EditAddressDialog({
  target,
  name,
  onNameChange,
  address,
  onAddressChange,
  locationId,
  onLocationChange,
  locations,
  isAdmin,
  busy,
  onSave,
  onClose,
}) {
  const [collision, setCollision] = useState(null);
  // Debounced name-collision check — fires 400ms after the user stops
  // typing so the admin sees inline "another brokerage has this name"
  // guidance before hitting Save. Non-blocking — save always allowed.
  useEffect(() => {
    if (!target || !name || name === target.name) {
      setCollision(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/brokerages/check-name-collision", {
          params: { name, exclude_id: target.id },
        });
        setCollision(r.data?.collision ? r.data.existing : null);
      } catch {
        setCollision(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [name, target]);

  return (
    <AlertDialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent data-testid="brokerage-edit-address-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-5 w-5" /> Edit brokerage
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600">
            Renaming updates every realtor's cached brokerage name.
            Editing the address flips its source to <strong>manual</strong>; future
            "Sync addresses" passes will leave this brokerage alone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-brokerage-name" className="text-xs uppercase tracking-wider text-slate-500">
              Name
            </Label>
            <Input
              id="edit-brokerage-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Brokerage name"
              className="min-h-[44px]"
              data-testid="brokerage-edit-name-input"
            />
            {collision ? (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"
                data-testid="brokerage-name-collision-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  An existing brokerage has this name (<em>{collision.name}</em>, {collision.realtor_count || 0} realtors).
                  Saving will not merge automatically — pick a distinguishing name if you want them kept separate.
                </span>
              </div>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-office-address" className="text-xs uppercase tracking-wider text-slate-500">
              Office address
            </Label>
            <Input
              id="edit-office-address"
              value={address}
              onChange={(e) => onAddressChange(e.target.value)}
              placeholder="e.g. 123 Main St, Saskatoon, SK S7K 1A1"
              className="min-h-[44px]"
              data-testid="brokerage-edit-address-input"
            />
          </div>
          {isAdmin && locations.length > 0 ? (
            <div className="pt-1">
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Location (workspace)
              </Label>
              <Select value={locationId} onValueChange={onLocationChange}>
                <SelectTrigger className="min-h-[44px] mt-1" data-testid="brokerage-edit-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} className="min-h-[44px]">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              onSave();
            }}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="brokerage-edit-address-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
